import {
  ClaimMethod,
  ClaimRequestStatus,
  type ClaimRequestViewDto,
} from '@up2stay/up2promo-api-client';
import {
  Badge,
  type BadgeProps,
  Button,
  type ButtonProps,
  ConfirmDialog,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  cn,
  ghostAccentIconTileClass,
  healthAttentionIconTileClass,
} from '@up2stay/up2promo-ui';
import { ChevronRight, Clock, FileText, Mail, XCircle } from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';

import {
  fillClaimTemplate,
  useClaimDict,
  useClaimRequestActions,
  useClaimTimeFormat,
  useNow,
} from '@/core/hooks/use-claim-requests';
import { useIntercom } from '@/core/hooks/use-intercom';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { buildProductsSetupPath } from '@/core/lib/build-products-setup-path';
import {
  claimResendAvailableAt,
  claimStatusKey,
  formatCountdown,
  isClaimResendLimitReached,
  readClaimApiError,
  type ClaimApiError,
} from '@/core/lib/claim-requests';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

const STATUS_BADGE: Record<string, BadgeProps['variant']> = {
  PENDING_EMAIL: 'warning',
  PENDING_MANUAL: 'info',
  VERIFIED: 'success',
  REJECTED: 'destructive',
  EXPIRED: 'secondary',
  CANCELLED: 'secondary',
  SUPERSEDED: 'secondary',
};

export function ClaimStatusBadge({
  request,
}: {
  request: Pick<ClaimRequestViewDto, 'status' | 'method'>;
}) {
  const t: Record<string, string> = useClaimDict().status;
  const key = claimStatusKey(request);
  return (
    <Badge variant={STATUS_BADGE[key] ?? 'secondary'}>{t[key] ?? key}</Badge>
  );
}

/** Inline copy for a failed resend / claim action. */
export function useClaimErrorMessage() {
  const t = useClaimDict().product;
  return useCallback(
    (err: ClaimApiError): string => {
      switch (err.code) {
        case 'claimResendLimit':
          return t.resendLimit;
        case 'claimResendCooldown':
          return fillClaimTemplate(t.resendCooldown, {
            seconds: err.retryAfterSeconds ?? 60,
          });
        case 'claimSuperseded':
          return t.supersededMessage;
        case 'claimPending':
          return t.claimPendingError;
        default:
          return t.error;
      }
    },
    [t],
  );
}

/**
 * Milliseconds left until `targetMs`, ticking once a second while `active`
 * and stopping at zero.
 */
function useCountdownMs(targetMs: number, active: boolean): number {
  const [left, setLeft] = useState(() => Math.max(0, targetMs - Date.now()));
  useEffect(() => {
    if (!active) return;
    let timer: ReturnType<typeof setInterval> | undefined;
    const tick = () => {
      const next = Math.max(0, targetMs - Date.now());
      setLeft(next);
      if (next === 0 && timer) clearInterval(timer);
    };
    const first = setTimeout(tick, 0);
    timer = setInterval(tick, 1000);
    return () => {
      clearTimeout(first);
      clearInterval(timer);
    };
  }, [targetMs, active]);
  return active ? left : 0;
}

/**
 * "Resend · 0:42" until the 60 s cooldown is over, then "Resend". Disabled at
 * the resend limit. Errors go to `onError` so callers can show them inline.
 */
export function ClaimResendButton({
  request,
  variant = 'outline',
  size,
  className,
  onError,
}: {
  request: ClaimRequestViewDto;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  className?: string;
  onError?: (error: ClaimApiError) => void;
}) {
  const t = useClaimDict().pending;
  const { resend } = useClaimRequestActions();
  const limitReached = isClaimResendLimitReached(request);
  const waitMs = useCountdownMs(claimResendAvailableAt(request), !limitReached);
  const cooling = waitMs > 0;

  const onClick = () => {
    resend.mutate(request.id, {
      onSuccess: () => toast.success(t.resent),
      onError: (err) => onError?.(readClaimApiError(err)),
    });
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        disabled={cooling || limitReached || resend.isPending}
        onClick={onClick}
      >
        {cooling
          ? fillClaimTemplate(t.resendIn, { time: formatCountdown(waitMs) })
          : t.resend}
      </Button>
      {/* Empty while cooling, so each cooldown end is announced again. */}
      <span className="sr-only" aria-live="polite">
        {cooling || limitReached ? '' : t.resendReady}
      </span>
    </>
  );
}

/** Cancel (EMAIL) / withdraw (MANUAL) a pending request, with a confirm. */
export function ClaimCancelButton({
  request,
  variant = 'ghost',
  size,
  className,
  label,
  onDone,
}: {
  request: ClaimRequestViewDto;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  className?: string;
  label?: string;
  onDone?: () => void;
}) {
  const t = useClaimDict().pending;
  const { cancel } = useClaimRequestActions();
  const errorMessage = useClaimErrorMessage();
  const [open, setOpen] = useState(false);
  const isManual = request.method === ClaimMethod.MANUAL;
  const text = label ?? (isManual ? t.withdraw : t.cancel);
  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        disabled={cancel.isPending}
        onClick={() => setOpen(true)}
      >
        {text}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={text}
        description={isManual ? t.withdrawConfirm : t.cancelConfirm}
        confirmLabel={text}
        cancelLabel={t.confirmKeep}
        destructive
        onConfirm={async () => {
          try {
            await cancel.mutateAsync(request.id);
          } catch (err) {
            // ConfirmDialog stays open on a rejection; say why.
            toast.error(errorMessage(readClaimApiError(err)));
            throw err;
          }
          toast.success(t.cancelled);
          onDone?.();
        }}
      />
    </>
  );
}

function RowIcon({ request }: { request: ClaimRequestViewDto }) {
  const isPending = request.status === ClaimRequestStatus.PENDING;
  const isRejected = request.status === ClaimRequestStatus.REJECTED;
  const Icon = isRejected
    ? XCircle
    : !isPending
      ? Clock
      : request.method === ClaimMethod.MANUAL
        ? FileText
        : Mail;
  const tone = isRejected
    ? 'bg-destructive/10 text-destructive'
    : isPending && request.method === ClaimMethod.EMAIL
      ? ghostAccentIconTileClass
      : 'bg-muted text-muted-foreground';
  return (
    <span
      className={cn(
        'grid size-9 shrink-0 place-items-center rounded-[10px]',
        tone,
      )}
      aria-hidden
    >
      <Icon className="size-4" />
    </span>
  );
}

function ClaimRequestDetail({
  request,
  now,
}: {
  request: ClaimRequestViewDto;
  now: number;
}): ReactNode {
  const t = useClaimDict().pending;
  const time = useClaimTimeFormat();
  const muted = 'text-xs text-muted-foreground';
  switch (request.status) {
    case ClaimRequestStatus.PENDING:
      return request.method === ClaimMethod.MANUAL ? (
        <>
          {request.note ? (
            <p className={cn(muted, 'break-words')}>„{request.note}“</p>
          ) : null}
          <p className={muted}>
            {fillClaimTemplate(t.submittedOn, {
              date: time.date(request.createdAt),
            })}
            {' · '}
            {t.reviewEta}
          </p>
        </>
      ) : (
        <>
          {request.maskedEmail ? (
            <p className={muted}>
              {fillClaimTemplate(t.sentTo, {
                email: request.maskedEmail,
                ago: time.ago(request.lastSentAt ?? request.createdAt, now),
              })}
            </p>
          ) : null}
          <p className={muted}>
            <time
              dateTime={request.expiresAt}
              title={time.dateTime(request.expiresAt)}
            >
              {fillClaimTemplate(t.expiresIn, {
                duration: time.duration(Date.parse(request.expiresAt) - now),
              })}
            </time>
          </p>
        </>
      );
    case ClaimRequestStatus.REJECTED:
      return request.rejectionReason ? (
        <p className="text-xs text-foreground">
          {fillClaimTemplate(t.rejectedReason, {
            reason: request.rejectionReason,
          })}
        </p>
      ) : null;
    case ClaimRequestStatus.EXPIRED:
      return <p className={muted}>{t.expiredMessage}</p>;
    case ClaimRequestStatus.SUPERSEDED:
      return <p className={muted}>{t.supersededMessage}</p>;
    case ClaimRequestStatus.CANCELLED:
      return <p className={muted}>{t.cancelledMessage}</p>;
    default:
      return null;
  }
}

function ClaimRequestRow({
  request,
  canManage,
  now,
  onNavigate,
}: {
  request: ClaimRequestViewDto;
  canManage: boolean;
  now: number;
  onNavigate: () => void;
}) {
  const claim = useClaimDict();
  const t = claim.pending;
  const intercom = useIntercom();
  const { navigateShell } = useNavigateShell();
  const errorMessage = useClaimErrorMessage();
  const [error, setError] = useState<string | null>(null);
  const actionSize = isCapacitorNative() ? 'lg' : 'sm';
  const isManual = request.method === ClaimMethod.MANUAL;

  const openSetup = () => {
    onNavigate();
    navigateShell(buildProductsSetupPath({ hash: request.hash }));
  };

  const openSupport = async () => {
    const prefill = claim.product.claimedNoEmailPrefill.replace(
      '{{hash}}',
      request.hash,
    );
    const opened = await intercom.openNewMessage(prefill);
    if (!opened) toast.error(claim.product.error);
  };

  let actions: ReactNode = null;
  if (canManage) {
    switch (request.status) {
      case ClaimRequestStatus.PENDING:
        actions = (
          <>
            {!isManual ? (
              <ClaimResendButton
                request={request}
                size={actionSize}
                onError={(err) => setError(errorMessage(err))}
              />
            ) : null}
            <ClaimCancelButton request={request} size={actionSize} />
          </>
        );
        break;
      case ClaimRequestStatus.REJECTED:
        actions = (
          <>
            <Button
              type="button"
              variant="outline"
              size={actionSize}
              onClick={openSetup}
            >
              {t.retry}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size={actionSize}
              onClick={() => void openSupport()}
            >
              {t.support}
            </Button>
          </>
        );
        break;
      case ClaimRequestStatus.EXPIRED:
      case ClaimRequestStatus.CANCELLED:
        actions = (
          <Button
            type="button"
            variant="outline"
            size={actionSize}
            onClick={openSetup}
          >
            {isManual ? t.retry : t.sendNew}
          </Button>
        );
        break;
      default:
        break;
    }
  }

  return (
    <li
      className="flex gap-3 border-t border-border px-4 py-3.5 first:border-t-0"
      data-claim-request-id={request.id}
    >
      <RowIcon request={request} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
            <span className="truncate rounded-md bg-muted px-1.5 font-mono text-xs font-medium">
              {request.hash}
            </span>
            <span className="truncate">
              · {isManual ? t.methodManual : t.methodEmail}
            </span>
          </span>
          <ClaimStatusBadge request={request} />
        </div>
        <div className="mt-0.5 flex flex-col gap-0.5">
          <ClaimRequestDetail request={request} now={now} />
        </div>
        {error ? (
          <p role="alert" className="mt-2 text-xs text-destructive">
            {error}
          </p>
        ) : null}
        {actions ? (
          <div className="mt-2.5 flex flex-wrap gap-2">{actions}</div>
        ) : null}
      </div>
    </li>
  );
}

function ClaimRequestGroup({
  title,
  requests,
  canManage,
  now,
  onNavigate,
}: {
  title: string;
  requests: ClaimRequestViewDto[];
  canManage: boolean;
  now: number;
  onNavigate: () => void;
}) {
  const headingId = useId();
  if (requests.length === 0) return null;
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <h3
        id={headingId}
        className="px-1 text-[11px] font-semibold tracking-[0.06em] text-muted-foreground uppercase"
      >
        {title}
      </h3>
      <ul className="overflow-hidden rounded-xl border border-border bg-card">
        {requests.map((request) => (
          <ClaimRequestRow
            key={request.id}
            request={request}
            canManage={canManage}
            now={now}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    </section>
  );
}

function LiveBadge() {
  const t = useClaimDict().pending;
  return (
    <Badge variant="success">
      <span className="size-1.5 rounded-full bg-current" aria-hidden />
      {t.live}
    </Badge>
  );
}

export type ClaimRequestsDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending: ClaimRequestViewDto[];
  recent: ClaimRequestViewDto[];
  canManage: boolean;
  live: boolean;
  contentId: string;
};

/**
 * Right-side panel in the browser, bottom sheet on narrow viewports
 * (DEV-756, direction B). Built on `ResponsiveDialog`: the desktop dialog is
 * re-positioned as a right panel, so no new primitive is needed.
 */
export function ClaimRequestsDrawer({
  open,
  onOpenChange,
  pending,
  recent,
  canManage,
  live,
  contentId,
}: ClaimRequestsDrawerProps) {
  const t = useClaimDict().pending;
  const isMobile = useIsMobile();
  const now = useNow(30_000, open);
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent
        id={contentId}
        className={cn(
          !isMobile &&
            'top-0 right-0 left-auto flex h-dvh max-h-none w-full max-w-[460px] translate-x-0 translate-y-0 flex-col gap-0 overflow-y-auto rounded-none border-y-0 border-r-0 border-l p-0 data-[state=closed]:slide-out-to-right data-[state=closed]:zoom-out-100 data-[state=open]:slide-in-from-right data-[state=open]:zoom-in-100',
        )}
      >
        <ResponsiveDialogHeader
          className={cn(!isMobile && 'px-5 pt-5 pb-3 text-left')}
        >
          <div className="flex items-center gap-2 pr-8">
            <ResponsiveDialogTitle className="text-[15.5px] font-bold">
              {t.drawerTitle}
            </ResponsiveDialogTitle>
            {live ? <LiveBadge /> : null}
          </div>
          <ResponsiveDialogDescription className="text-xs">
            {t.info}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        <div
          className={cn('flex flex-col gap-4 pb-6', isMobile ? 'pt-2' : 'px-5')}
        >
          <ClaimRequestGroup
            title={t.sectionPending}
            requests={pending}
            canManage={canManage}
            now={now}
            onNavigate={close}
          />
          <ClaimRequestGroup
            title={t.sectionRecent}
            requests={recent}
            canManage={canManage}
            now={now}
            onNavigate={close}
          />
          <p className="px-1 text-xs text-muted-foreground">{t.footer}</p>
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

/**
 * Status strip under the Produkte header: pending count plus rejected /
 * expired counts. Opens {@link ClaimRequestsDrawer}. Hidden when both
 * sections are empty.
 */
export function ClaimRequestsStrip({
  pending,
  recent,
  canManage,
  live,
}: {
  pending: ClaimRequestViewDto[];
  recent: ClaimRequestViewDto[];
  canManage: boolean;
  live: boolean;
}) {
  const t = useClaimDict().pending;
  const [open, setOpen] = useState(false);
  const contentId = useId();
  const empty = pending.length === 0 && recent.length === 0;
  // An open drawer stays until it is closed, even when its last row left.
  if (empty && !open) return null;

  const rejected = recent.filter(
    (r) => r.status === ClaimRequestStatus.REJECTED,
  ).length;
  const expired = recent.filter(
    (r) => r.status === ClaimRequestStatus.EXPIRED,
  ).length;
  const title =
    pending.length === 0
      ? t.stripNone
      : pending.length === 1
        ? t.stripOne
        : fillClaimTemplate(t.strip, { count: pending.length });
  const secondary = [
    rejected > 0 ? fillClaimTemplate(t.stripRejected, { count: rejected }) : '',
    expired > 0 ? fillClaimTemplate(t.stripExpired, { count: expired }) : '',
  ].filter(Boolean);

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? contentId : undefined}
        onClick={() => setOpen(true)}
        className={cn(
          'flex min-h-11 w-full items-center gap-3 rounded-2xl border bg-card py-2.5 pr-3 pl-3.5 text-left shadow-xs transition-colors hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
          pending.length > 0
            ? 'border-[color:var(--warn-border)]'
            : 'border-border',
        )}
        data-testid="claim-requests-strip"
      >
        <span
          className={cn(
            'grid size-8 shrink-0 place-items-center rounded-[10px]',
            healthAttentionIconTileClass,
          )}
          aria-hidden
        >
          <Clock className="size-4" />
        </span>
        <span className="min-w-0 flex-1 text-sm">
          <span className="font-semibold">{title}</span>
          {secondary.length > 0 ? (
            <span className="text-muted-foreground">
              {' · '}
              {secondary.join(' · ')}
            </span>
          ) : null}
        </span>
        {live ? (
          <span className="hidden sm:inline-flex">
            <LiveBadge />
          </span>
        ) : null}
        <span
          className="inline-flex h-8 items-center gap-1 rounded-[10px] border border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)] px-2.5 text-xs font-semibold text-[color:var(--ghost-accent-fg)]"
          aria-hidden
        >
          {t.show}
          <ChevronRight className="size-3.5" />
        </span>
      </button>
      <ClaimRequestsDrawer
        open={open}
        onOpenChange={setOpen}
        pending={pending}
        recent={recent}
        canManage={canManage}
        live={live}
        contentId={contentId}
      />
    </>
  );
}
